/**
 * Sparkline — shared per-week capacity SVG bar chart.
 *
 * Originally lived inside RosterView; extracted in v260.83 so the
 * "My Estimates" personal dashboard can render the same visualisation
 * with the same colour story.
 *
 * Props:
 *   weeklyHours      number[]    per-week hours allocated to this person
 *   capacityPerWeek  number      weekly capacity benchmark (drawn as dashed line)
 *   weekLabels       string[]?   optional ISO/MM-DD labels for the tooltip
 *   todayIdx         number?     0-indexed week of "today" (vertical navy line)
 *   width            number?     SVG viewBox width (defaults 360)
 *   height           number?     SVG viewBox height (defaults 36)
 *   testid           string?     data-testid override
 */
import React from 'react';

export default function Sparkline({
  weeklyHours,
  capacityPerWeek,
  weekLabels,
  todayIdx,
  blockedIndices,
  width = 360,
  height = 36,
  testid = 'sparkline',
}) {
  if (!weeklyHours || weeklyHours.length === 0) return null;
  const W = width;
  const H = height;
  const PAD_X = 2;
  const PAD_Y = 3;
  const n = weeklyHours.length;
  const barW = (W - PAD_X * 2) / n;
  const cap = Math.max(capacityPerWeek || 0, 0.001);
  const blockedSet = new Set(blockedIndices || []);

  // y-axis scale: peak of (max load, 1.5× capacity) so capacity line stays visible
  const peak = Math.max(cap * 1.5, ...weeklyHours.map(h => h || 0), 0.001);
  const yFor = (h) => H - PAD_Y - ((h / peak) * (H - PAD_Y * 2));
  const capY = yFor(cap);

  const colorFor = (h, idx) => {
    if (blockedSet.has(idx)) return '#CBD5E1';  // slate-300 — PTO / blocked
    if (h <= 0) return '#E2E8F0';            // slate-200 — idle
    const ratio = h / cap;
    if (ratio < 0.5) return '#A7F3D0';       // emerald-200 — light
    if (ratio < 1.0) return '#34D399';       // emerald-400 — healthy
    if (ratio < 1.5) return '#F59E0B';       // amber-500 — loaded
    return '#EF4444';                        // rose-500 — overbooked
  };

  return (
    <svg
      width="100%"
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ display: 'block' }}
      data-testid={testid}
    >
      {/* Capacity line — the "100% utilised at this person's weekly hours" benchmark */}
      <line
        x1={PAD_X}
        x2={W - PAD_X}
        y1={capY}
        y2={capY}
        stroke="#94A3B8"
        strokeWidth="1"
        strokeDasharray="2 2"
      />
      {/* Per-week bars */}
      {weeklyHours.map((h, i) => {
        const x = PAD_X + i * barW;
        const isBlocked = blockedSet.has(i);
        // For blocked weeks render a full-height grey block so the
        // "out of office" signal is visible regardless of underlying load.
        const y = isBlocked ? PAD_Y : yFor(h);
        const heightPx = isBlocked ? (H - PAD_Y * 2) : Math.max(1, H - PAD_Y - y);
        const label = weekLabels?.[i] || `W${i + 1}`;
        const tooltip = isBlocked
          ? `${label}: PTO / blocked week`
          : `${label}: ${(h || 0).toFixed(1)}h / ${cap.toFixed(0)}h cap`;
        return (
          <rect
            key={i}
            x={x + 0.5}
            y={y}
            width={Math.max(1, barW - 1)}
            height={heightPx}
            fill={colorFor(h, i)}
            fillOpacity={isBlocked ? 0.55 : 1}
            rx="1"
          >
            <title>{tooltip}</title>
          </rect>
        );
      })}
      {/* Today marker */}
      {typeof todayIdx === 'number' && todayIdx >= 0 && todayIdx < n && (
        <line
          x1={PAD_X + todayIdx * barW + barW / 2}
          x2={PAD_X + todayIdx * barW + barW / 2}
          y1={0}
          y2={H}
          stroke="#0F2040"
          strokeWidth="1"
          opacity="0.45"
        />
      )}
    </svg>
  );
}
